import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@codes/ui-kit/components/ui/card';
import { Image } from '@codes/ui-kit/components/ui/image';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { SaveSecretBody, WorkspaceView } from '@lemoncloud/codes-goods-api';
import { useQueryClient } from '@tanstack/react-query';
import {
    AwsLogo,
    CopyButton,
    DangerZone,
    GeminiKeyInfoContent,
    GeminiLogo,
    KeyCard,
    OpenAIKeyInfoContent,
    OpenAILogo,
    SectionHeader,
} from '@web/app/features/home/components/settings';
import { useProjectService, useWorkspaceService } from '@web/app/features/home/hooks';
import { GuardScope, useAccessGuard, useSelectedStore, useUpdateQueryData } from '@web/app/shared';
import { AlertTriangle, Info, RefreshCw, X } from 'lucide-react';

import { KeyInfoDarkImage, KeyInfoImage } from '@codes/assets';
import { useRegions } from '@codes/cloud';
import { useOwnerStore } from '@codes/owner';
import { useProjects } from '@codes/project';
import { PaginationType } from '@codes/shared';
import {
    Button,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
    SharedSpinner,
    Sheet,
    SheetClose,
    SheetContent,
    SheetHeader,
    Skeleton,
    cn,
} from '@codes/ui-kit';
import {
    useDeleteWorkspace,
    useEnrollAccessKeyWithWorkspaceId,
    useUpdateWorkspace,
    useUpdateWorkspaceGeminiKey,
    useUpdateWorkspaceOpenAIKey,
    workspacesKeys,
} from '@codes/workspace';

const DEFAULT_SECRET_BODY: SaveSecretBody = {
    accessKey: '',
    secretKey: '',
};

export const WorkspaceSettingsPage = () => {
    useAccessGuard(GuardScope.Workspace, true);

    const { setWorkspace, setProject } = useSelectedStore();
    const { workspaceFromServer, isOwner, workspaceFromServerQueryData } = useWorkspaceService();
    const { areMyProjectsEmpty, myProjectsQueryResult } = useProjectService();

    const updateWorkspaceQueryData = useUpdateQueryData<PaginationType<WorkspaceView[]>>();
    const { owner } = useOwnerStore();

    const queryClient = useQueryClient();

    const { t } = useTranslation();

    const workspace = workspaceFromServer;

    const { mutateAsync: updateWorkspace, data: updatedWorkspace, isPending: isUpdating } = useUpdateWorkspace();
    const { mutateAsync: deleteWorkspace, data: deletedWorkspace, isPending: isDeleting } = useDeleteWorkspace();

    const {
        mutateAsync: enrollAccessKeyWithWorkspaceId,
        data: workspaceWithSecret,
        isPending: isAccessKeyEnrolling,
        reset: resetWorkspaceWithSecret,
        isError: isEnrollAccessKeyError,
    } = useEnrollAccessKeyWithWorkspaceId();

    const {
        mutateAsync: updateGeminiKeyMutation,
        isPending: isGeminiKeyUpdating,
        isSuccess: isGeminiKeyUpdated,
        isError: isGeminiKeyError,
        reset: resetGeminiKeyMutation,
    } = useUpdateWorkspaceGeminiKey();

    const {
        mutateAsync: updateOpenAIKeyMutation,
        isPending: isOpenAIKeyUpdating,
        isSuccess: isOpenAIKeyUpdated,
        isError: isOpenAIKeyError,
        reset: resetOpenAIKeyMutation,
    } = useUpdateWorkspaceOpenAIKey();

    const haveAccessKey = !!workspace?.secret$ && !!workspace.secret$.accountNo;
    const haveGeminiKey = !!workspace?.geminiApiKey;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const haveOpenAIKey = !!(workspace as any)?.openaiApiKey;
    const { data: projectsData } = useProjects(workspace?.id as string, { limit: 50, page: 0 });
    const hasNoProject = projectsData?.total === 0;
    const {
        data: regions,
        isLoading: isRegionsLoading,
        isError: isRegionsError,
        refetch: refetchRegions,
    } = useRegions({ limit: 100, cloudId: workspace?.cloudId });

    const [workspaceName, setWorkspaceName] = useState(workspace?.name || '');
    const [workspaceRegionId, setWorkspaceRegionId] = useState(workspace?.regionId || '');
    const [isKeyInfoOpen, setIsKeyInfoOpen] = useState(false);
    const [isKeyFormOpen, setIsKeyFormOpen] = useState(false);
    const [saveSecretBody, setSaveSecretBody] = useState<SaveSecretBody>({ ...DEFAULT_SECRET_BODY });
    const [accessKeyEditMode, setAccessKeyEditMode] = useState(false);
    const [geminiApiKeyEditMode, setGeminiApiKeyEditMode] = useState(false);
    const [isGeminiKeyFormOpen, setIsGeminiKeyFormOpen] = useState(false);
    const [geminiApiKeyValue, setGeminiApiKeyValue] = useState('');
    const [isGeminiKeyInfoOpen, setIsGeminiKeyInfoOpen] = useState(false);
    const [openaiApiKeyEditMode, setOpenaiApiKeyEditMode] = useState(false);
    const [isOpenAIKeyFormOpen, setIsOpenAIKeyFormOpen] = useState(false);
    const [openaiApiKeyValue, setOpenaiApiKeyValue] = useState('');
    const [isOpenAIKeyInfoOpen, setIsOpenAIKeyInfoOpen] = useState(false);

    const isWorkspaceInputDisabled = workspaceName === workspace?.name || !workspaceName || isUpdating;
    const isUpdateRegionBtnDisabled = workspaceRegionId === workspace?.regionId || !workspaceRegionId || isUpdating;
    const isUpdateAccessBtnDisabled = !saveSecretBody.accessKey || !saveSecretBody.secretKey || isAccessKeyEnrolling;

    const handleUpdateWorkspaceNameBtnClick = () => {
        if (!workspace) {
            return;
        }
        updateWorkspace({ ...workspace, workspaceId: workspace.id as string, name: workspaceName });
    };

    const handleUpdateWorkspaceRegionBtnClick = () => {
        if (!workspace) {
            return;
        }
        updateWorkspace({ workspaceId: workspace.id as string, regionId: workspaceRegionId });
    };

    const handleDeleteWorkspaceBtnClick = () => {
        if (!workspace || !owner || !!myProjectsQueryResult.error) {
            return;
        }

        const isDefault = workspace.id === owner.defWorkspaceId;
        if (isDefault) {
            toast({ description: t('workspace.setting.deleteDefaultWorkspaceNotAllowed') });
            return;
        }

        if (!isOwner) {
            toast({ description: t('workspace.setting.deleteWorkspaceNotAllowedForNonOwner') });
            return;
        }

        if (!areMyProjectsEmpty) {
            toast({ description: t('workspace.setting.workspaceDeletionNotAllowedDueToProjects') });
            return;
        }

        const isOkay = confirm(t('workspace.setting.confirmToDeleteWorkspace'));
        if (!isOkay) {
            return;
        }

        deleteWorkspace(workspace.id as string);
    };

    const handleCompleteToUpdateWorkspace = (updatedWorkspace: WorkspaceView) => {
        setWorkspace(updatedWorkspace);

        toast({ description: t('workspace.setting.completeToUpdateWorkspace') });

        queryClient.setQueryData<WorkspaceView>(workspacesKeys.detail('0'), oldData => {
            if (!oldData) {
                return updatedWorkspace;
            }
            return { ...oldData, ...updatedWorkspace };
        });

        updateWorkspaceQueryData({
            queryKey: workspacesKeys.list({}),
            updater: oldData => {
                if (!oldData) {
                    return { total: 1, data: [updatedWorkspace], list: [updatedWorkspace], page: 0 };
                }

                const updatedData = oldData.data.map(oldWorkspace => {
                    if (oldWorkspace.id === updatedWorkspace.id) {
                        return updatedWorkspace;
                    }
                    return oldWorkspace;
                });

                return {
                    ...oldData,
                    data: updatedData,
                    list: updatedData,
                };
            },
        });
    };

    const handleCompleteToDeleteWorkspace = (deletedWorkspace: WorkspaceView) => {
        setWorkspace(null);
        setProject(null);
        toast({ description: t('workspace.setting.completeToDeleteWorkspace') });

        updateWorkspaceQueryData({
            queryKey: workspacesKeys.list({}),
            updater: oldData => {
                if (!oldData) {
                    return { total: 0, data: [], list: [], page: 0 };
                }

                const updatedData = oldData.data.filter(oldWorkspace => {
                    return oldWorkspace.id !== deletedWorkspace.id;
                });

                return {
                    ...oldData,
                    data: updatedData,
                    list: updatedData,
                    total: (oldData.total || 1) - 1,
                };
            },
        });
    };

    const projectsNameLabel = useMemo(() => {
        if (!projectsData || !projectsData.data || projectsData.data.length === 0) {
            return null;
        }
        const project = projectsData.data[0];
        const projectsCount = projectsData.total;

        if (projectsCount === 1) {
            return <div className="text-base text-sub-title font-medium">{project.name}</div>;
        }

        return (
            <div className="text-base text-sub-title font-medium">
                {t('project.projectCountLabel', { projectName: project.name, count: (projectsCount || 1) - 1 })}
            </div>
        );
    }, [projectsData]);

    const enrollAccessKeyBtnTapped = () => {
        if (isUpdateAccessBtnDisabled) {
            return;
        }
        if (workspaceWithSecret) {
            setIsKeyFormOpen(false);
            return;
        }
        if (!workspace || !workspace.id) {
            return;
        }
        enrollAccessKeyWithWorkspaceId({ body: saveSecretBody, workspaceId: workspace.id });
    };

    const enterAccessKeyBtnTapped = () => {
        setIsKeyFormOpen(true);
        setAccessKeyEditMode(!haveAccessKey);
    };

    const enterGeminiKeyBtnTapped = () => {
        setIsGeminiKeyFormOpen(true);
        setGeminiApiKeyEditMode(!haveGeminiKey);
        if (haveGeminiKey && workspace?.geminiApiKey) {
            setGeminiApiKeyValue(workspace.geminiApiKey);
        }
    };

    const saveGeminiKeyBtnTapped = async () => {
        if (!workspace?.id || !geminiApiKeyValue.trim()) {
            return;
        }

        await updateGeminiKeyMutation({
            workspaceId: workspace.id,
            geminiApiKey: geminiApiKeyValue.trim(),
        });
    };

    const cancelGeminiKeyEdit = () => {
        setGeminiApiKeyEditMode(false);
        setGeminiApiKeyValue(workspace?.geminiApiKey || '');
    };

    const enterOpenAIKeyBtnTapped = () => {
        setIsOpenAIKeyFormOpen(true);
        setOpenaiApiKeyEditMode(!haveOpenAIKey);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (haveOpenAIKey && (workspace as any)?.openaiApiKey) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setOpenaiApiKeyValue((workspace as any).openaiApiKey);
        }
    };

    const saveOpenAIKeyBtnTapped = async () => {
        if (!workspace?.id || !openaiApiKeyValue.trim()) {
            return;
        }

        await updateOpenAIKeyMutation({
            workspaceId: workspace.id,
            openaiApiKey: openaiApiKeyValue.trim(),
        });
    };

    const cancelOpenAIKeyEdit = () => {
        setOpenaiApiKeyEditMode(false);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setOpenaiApiKeyValue((workspace as any)?.openaiApiKey || '');
    };

    useEffect(() => {
        if (!workspace) {
            return;
        }
        setWorkspaceName(workspace.name as string);
        setWorkspaceRegionId(workspace.regionId as string);
    }, [workspace]);

    useEffect(() => {
        if (!updatedWorkspace) {
            return;
        }
        handleCompleteToUpdateWorkspace(updatedWorkspace);
    }, [updatedWorkspace]);

    useEffect(() => {
        if (!deletedWorkspace) {
            return;
        }
        handleCompleteToDeleteWorkspace(deletedWorkspace);
    }, [deletedWorkspace]);

    useEffect(() => {
        if (isKeyFormOpen) {
            setSaveSecretBody({ ...DEFAULT_SECRET_BODY });
            return;
        }
        resetWorkspaceWithSecret();
    }, [isKeyFormOpen]);

    useEffect(() => {
        if (!workspaceWithSecret) {
            return;
        }
        setWorkspace(workspaceWithSecret);
    }, [workspaceWithSecret]);

    useEffect(() => {
        if (isGeminiKeyFormOpen) {
            setGeminiApiKeyValue(workspace?.geminiApiKey || '');
            resetGeminiKeyMutation();
        }
    }, [isGeminiKeyFormOpen, workspace?.geminiApiKey, resetGeminiKeyMutation]);

    useEffect(() => {
        if (isGeminiKeyUpdated && isGeminiKeyFormOpen) {
            setGeminiApiKeyEditMode(false);
            setIsGeminiKeyFormOpen(false);
        }
    }, [isGeminiKeyUpdated, isGeminiKeyFormOpen]);

    useEffect(() => {
        if (isOpenAIKeyFormOpen) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setOpenaiApiKeyValue((workspace as any)?.openaiApiKey || '');
            resetOpenAIKeyMutation();
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }, [isOpenAIKeyFormOpen, (workspace as any)?.openaiApiKey, resetOpenAIKeyMutation]);

    useEffect(() => {
        if (isOpenAIKeyUpdated && isOpenAIKeyFormOpen) {
            setOpenaiApiKeyEditMode(false);
            setIsOpenAIKeyFormOpen(false);
        }
    }, [isOpenAIKeyUpdated, isOpenAIKeyFormOpen]);

    if (workspaceFromServerQueryData.isFetching || workspaceFromServerQueryData.isRefetching) {
        return (
            <div className="flex flex-col py-8 px-8 max-md:p-5 w-full items-center">
                <div className="w-full max-w-4xl">
                    <div className="text-xl font-semibold mb-8">{t('workspace.setting.title')}</div>
                    <div className="space-y-7">
                        <Card className="py-6 px-5 rounded-2xl">
                            <Skeleton className="h-4 w-32 mb-4" />
                            <div className="flex flex-col gap-y-[22px]">
                                <Skeleton className="h-10 w-full" />
                                <Skeleton className="h-10 w-full" />
                            </div>
                        </Card>
                        <Card className="py-6 px-5 rounded-2xl">
                            <Skeleton className="h-4 w-24 mb-4" />
                            <div className="flex flex-col gap-y-[22px]">
                                <Skeleton className="h-10 w-full" />
                                <Skeleton className="h-10 w-full" />
                            </div>
                        </Card>
                        <Card className="py-6 px-5 rounded-2xl">
                            <Skeleton className="h-4 w-20 mb-4" />
                            <Skeleton className="h-10 w-full" />
                        </Card>
                    </div>
                </div>
            </div>
        );
    }

    if (workspaceFromServerQueryData.isError) {
        return (
            <div className="flex flex-col py-8 px-8 max-md:p-5 w-full items-center">
                <div className="w-full max-w-4xl">
                    <div className="text-xl font-semibold mb-8">{t('workspace.setting.title')}</div>
                    <div className="flex flex-col items-center gap-6 py-12">
                        <div className="w-16 h-16 rounded-full bg-error/15 flex items-center justify-center">
                            <AlertTriangle className="w-8 h-8 text-error" />
                        </div>
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-text mb-2">{t('workspace.error.fetchDetail')}</h3>
                            <p className="text-sub">{t('common.retry')}</p>
                        </div>
                        <Button
                            onClick={() => workspaceFromServerQueryData.refetch()}
                            disabled={workspaceFromServerQueryData.isRefetching}
                            className="gap-2"
                        >
                            <RefreshCw
                                className={`w-4 h-4 ${workspaceFromServerQueryData.isRefetching ? 'animate-spin' : ''}`}
                            />
                            {t('workspace.error.retry')}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col py-6 px-4 md:py-10 md:px-8 w-full items-center animate-enter">
                <div className="w-full max-w-4xl space-y-8">
                    {/* Page Header */}
                    <div className="mb-2">
                        <h1 className="text-xl font-semibold font-display tracking-tight">
                            {t('workspace.setting.title')}
                        </h1>
                    </div>

                    {/* General Settings Section */}
                    <div className="space-y-4">
                        <SectionHeader title={t('workspace.setting.generalSettings') || 'General Settings'} />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Card className="p-5">
                                <Label variant="form" className="text-sm font-medium mb-2 block">
                                    {t('workspace.setting.labelText')}
                                </Label>
                                <div className="flex gap-2 items-end">
                                    <Input
                                        type="text"
                                        value={workspaceName}
                                        onChange={e => setWorkspaceName(e.target.value)}
                                        className="flex-1"
                                    />
                                    <Button
                                        disabled={isWorkspaceInputDisabled}
                                        onClick={handleUpdateWorkspaceNameBtnClick}
                                        className="shrink-0"
                                    >
                                        {t('project.edit')}
                                    </Button>
                                </div>
                            </Card>

                            <Card className="p-5">
                                <Label variant="form" className="text-sm font-medium mb-2 block">
                                    {t('workspace.setting.regionLabelText')}
                                </Label>
                                <div className="flex gap-2 items-end">
                                    {isRegionsLoading ? (
                                        <Skeleton className="h-10 w-full" />
                                    ) : isRegionsError ? (
                                        <div className="flex items-center gap-2 p-3 border border-error/20 rounded-lg bg-error/5">
                                            <AlertTriangle className="w-4 h-4 text-error" />
                                            <span className="text-sm text-error flex-1">
                                                {t('workspace.setting.regionLoadError')}
                                            </span>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => refetchRegions()}
                                                className="h-8 px-3"
                                            >
                                                <RefreshCw className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <Select
                                            defaultValue={workspace?.region$?.id}
                                            value={workspaceRegionId}
                                            onValueChange={regionId => setWorkspaceRegionId(regionId)}
                                            disabled={!hasNoProject}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {regions?.list.map(region => (
                                                    <SelectItem key={region.id} value={region.id as string}>
                                                        {region.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                    <Button
                                        disabled={isUpdateRegionBtnDisabled || isRegionsLoading || isRegionsError}
                                        onClick={handleUpdateWorkspaceRegionBtnClick}
                                        className="shrink-0"
                                    >
                                        {t('project.edit')}
                                    </Button>
                                </div>
                            </Card>
                        </div>
                    </div>

                    {/* Integration Keys Section */}
                    {workspace?.stereo !== 'basic' && (
                        <div className="space-y-4">
                            <SectionHeader title={t('workspace.setting.integrationKeys') || 'Integration Keys'} />

                            <div className="space-y-3">
                                <KeyCard
                                    title={t('workspace.setting.accessKeyLabelText')}
                                    description={
                                        t('workspace.setting.accessKey.description') ||
                                        'AWS credentials for cloud resource management'
                                    }
                                    isRegistered={haveAccessKey}
                                    onManage={enterAccessKeyBtnTapped}
                                    icon={<AwsLogo className="w-full h-full" />}
                                    brand="aws"
                                />

                                <KeyCard
                                    title={t('workspace.setting.geminiApiKey.label')}
                                    description={t('workspace.setting.geminiApiKey.description')}
                                    isRegistered={haveGeminiKey}
                                    onManage={enterGeminiKeyBtnTapped}
                                    icon={<GeminiLogo className="w-full h-full" />}
                                    brand="gemini"
                                />

                                <KeyCard
                                    title={t('workspace.setting.openaiApiKey.label')}
                                    description={t('workspace.setting.openaiApiKey.description')}
                                    isRegistered={haveOpenAIKey}
                                    onManage={enterOpenAIKeyBtnTapped}
                                    icon={
                                        <OpenAILogo className="w-full h-full text-emerald-700 dark:text-emerald-400" />
                                    }
                                    brand="openai"
                                />
                            </div>
                        </div>
                    )}

                    {workspace?.stereo === 'basic' && (
                        <div className="space-y-4">
                            <SectionHeader title={t('workspace.setting.integrationKeys') || 'Integration Keys'} />

                            <div className="space-y-3">
                                <KeyCard
                                    title={t('workspace.setting.geminiApiKey.label')}
                                    description={t('workspace.setting.geminiApiKey.description')}
                                    isRegistered={haveGeminiKey}
                                    onManage={enterGeminiKeyBtnTapped}
                                    icon={<GeminiLogo className="w-full h-full" />}
                                    brand="gemini"
                                />

                                <KeyCard
                                    title={t('workspace.setting.openaiApiKey.label')}
                                    description={t('workspace.setting.openaiApiKey.description')}
                                    isRegistered={haveOpenAIKey}
                                    onManage={enterOpenAIKeyBtnTapped}
                                    icon={
                                        <OpenAILogo className="w-full h-full text-emerald-700 dark:text-emerald-400" />
                                    }
                                    brand="openai"
                                />
                            </div>
                        </div>
                    )}

                    {/* Danger Zone Section */}
                    <div className="space-y-4">
                        <DangerZone
                            title={t('workspace.setting.workspaceDeletionLabelText')}
                            warning={t('workspace.setting.workspaceDeletionContentText')}
                            entityName={workspace?.name || ''}
                            entityInfo={projectsNameLabel}
                            onDelete={handleDeleteWorkspaceBtnClick}
                            deleteButtonText={t('project.delete')}
                            isDeleting={isDeleting}
                            isDeleteDisabled={!hasNoProject}
                            disabledReason={!hasNoProject ? t('workspace.setting.mustDeleteProjectsFirst') : undefined}
                        />
                    </div>
                </div>
            </div>

            <Dialog open={isKeyFormOpen} onOpenChange={setIsKeyFormOpen}>
                <DialogContent className="p-0" aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle>{t('workspace.create.accessKeyEnterButtonText')}</DialogTitle>
                    </DialogHeader>
                    <div className="py-5 px-4">
                        <Label variant="form" className="flex items-center gap-1">
                            {t('workspace.create.manualAccessKeyLabelText')}
                            <button onClick={() => setIsKeyInfoOpen(true)}>
                                <Info className="w-[14px] h-[14px] text-placeholder" />
                            </button>
                        </Label>
                        <Input
                            disabled={!accessKeyEditMode}
                            value={
                                accessKeyEditMode
                                    ? saveSecretBody.accessKey
                                    : workspaceFromServer?.secret$?.['accessKey']
                            }
                            onChange={e => setSaveSecretBody(prev => ({ ...prev, accessKey: e.target.value }))}
                            placeholder={t('workspace.create.enterSecretAccessKey')}
                            className={isEnrollAccessKeyError ? 'border-error' : ''}
                        />
                        <div className="my-9">
                            <Label variant="form">{t('workspace.create.secretAccessKeyLabelText')}</Label>
                            <Input
                                disabled={!accessKeyEditMode}
                                value={
                                    accessKeyEditMode
                                        ? saveSecretBody.secretKey
                                        : workspaceFromServer?.secret$?.['decryptKey']
                                }
                                type="password"
                                onChange={e => setSaveSecretBody(prev => ({ ...prev, secretKey: e.target.value }))}
                                placeholder={t('workspace.create.enterSecretAccessKey')}
                                className={isEnrollAccessKeyError ? 'border-error' : ''}
                            />
                        </div>
                        {isEnrollAccessKeyError && (
                            <div className="mb-10 text-center text-error">{t('accessKey.errorMessageText')}</div>
                        )}
                        {workspaceWithSecret && (
                            <div className="relative mt-5 mb-10">
                                <Label variant="form">Account ID</Label>
                                <Input value={workspaceWithSecret?.$secret?.accountNo} />
                                <div className="absolute -top-8 h-8 py-1 px-2 rounded-md text-white bg-text inline">
                                    <div
                                        className="absolute bottom-[-6px] left-1/2 transform -translate-x-1/2 w-0 h-0
                border-l-[5px] border-r-[5px] border-t-[7px] border-t-[#081837] border-transparent"
                                    ></div>
                                    {t('workspace.create.checkId')}
                                </div>
                            </div>
                        )}
                        {accessKeyEditMode ? (
                            <Button
                                size="lg"
                                className="w-full"
                                onClick={enrollAccessKeyBtnTapped}
                                disabled={isUpdateAccessBtnDisabled}
                            >
                                {isAccessKeyEnrolling ? (
                                    <SharedSpinner />
                                ) : (
                                    t(
                                        workspaceWithSecret
                                            ? 'workspace.create.complete'
                                            : 'workspace.create.authenticateKey'
                                    )
                                )}
                            </Button>
                        ) : (
                            <Button className="w-full" onClick={() => setAccessKeyEditMode(true)}>
                                {t('accessKey.editButtonText')}
                            </Button>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isGeminiKeyFormOpen} onOpenChange={setIsGeminiKeyFormOpen}>
                <DialogContent className="p-0" aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle>{t('workspace.setting.geminiApiKey.title')}</DialogTitle>
                    </DialogHeader>
                    <div className="py-5 px-4">
                        <Label variant="form" className="flex items-center gap-1">
                            {t('workspace.setting.geminiApiKey.label')}
                            <button onClick={() => setIsGeminiKeyInfoOpen(true)}>
                                <Info className="w-[14px] h-[14px] text-placeholder" />
                            </button>
                        </Label>
                        <div className="relative">
                            <Input
                                type="password"
                                disabled={!geminiApiKeyEditMode}
                                value={geminiApiKeyValue}
                                onChange={e => setGeminiApiKeyValue(e.target.value)}
                                placeholder={t('workspace.setting.geminiApiKey.placeholder')}
                                className={cn(
                                    isGeminiKeyError ? 'border-error' : '',
                                    geminiApiKeyValue && !geminiApiKeyEditMode ? 'pr-12' : ''
                                )}
                            />
                            {!geminiApiKeyEditMode && geminiApiKeyValue.trim() && (
                                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                    <CopyButton value={geminiApiKeyValue} />
                                </div>
                            )}
                        </div>
                        <div className="mt-9">
                            {geminiApiKeyEditMode ? (
                                <Button
                                    size="lg"
                                    className="w-full"
                                    onClick={saveGeminiKeyBtnTapped}
                                    disabled={!geminiApiKeyValue.trim() || isGeminiKeyUpdating}
                                >
                                    {isGeminiKeyUpdating ? (
                                        <SharedSpinner />
                                    ) : (
                                        t('workspace.setting.geminiApiKey.saveButton')
                                    )}
                                </Button>
                            ) : (
                                <Button className="w-full" onClick={() => setGeminiApiKeyEditMode(true)}>
                                    {t('workspace.setting.geminiApiKey.editButton')}
                                </Button>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Sheet open={isKeyInfoOpen} onOpenChange={setIsKeyInfoOpen}>
                <SheetContent className="overflow-auto" aria-describedby={undefined}>
                    <SheetHeader className="justify-end">
                        <SheetClose>
                            <X className="w-5 h-5" />
                        </SheetClose>
                    </SheetHeader>
                    <Image className="w-full mt-5" src={KeyInfoImage} darkSrc={KeyInfoDarkImage} />
                </SheetContent>
            </Sheet>

            <Sheet open={isGeminiKeyInfoOpen} onOpenChange={setIsGeminiKeyInfoOpen}>
                <SheetContent className="overflow-auto" aria-describedby={undefined}>
                    <SheetHeader className="justify-end">
                        <SheetClose>
                            <X className="w-5 h-5" />
                        </SheetClose>
                    </SheetHeader>
                    <GeminiKeyInfoContent />
                </SheetContent>
            </Sheet>

            <Dialog open={isOpenAIKeyFormOpen} onOpenChange={setIsOpenAIKeyFormOpen}>
                <DialogContent className="p-0" aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle>{t('workspace.setting.openaiApiKey.title')}</DialogTitle>
                    </DialogHeader>
                    <div className="py-5 px-4">
                        <Label variant="form" className="flex items-center gap-1">
                            {t('workspace.setting.openaiApiKey.label')}
                            <button onClick={() => setIsOpenAIKeyInfoOpen(true)}>
                                <Info className="w-[14px] h-[14px] text-placeholder" />
                            </button>
                        </Label>
                        <div className="relative">
                            <Input
                                type="password"
                                disabled={!openaiApiKeyEditMode}
                                value={openaiApiKeyValue}
                                onChange={e => setOpenaiApiKeyValue(e.target.value)}
                                placeholder={t('workspace.setting.openaiApiKey.placeholder')}
                                className={cn(
                                    isOpenAIKeyError ? 'border-error' : '',
                                    openaiApiKeyValue && !openaiApiKeyEditMode ? 'pr-12' : ''
                                )}
                            />
                            {!openaiApiKeyEditMode && openaiApiKeyValue.trim() && (
                                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                    <CopyButton value={openaiApiKeyValue} />
                                </div>
                            )}
                        </div>
                        <div className="mt-9">
                            {openaiApiKeyEditMode ? (
                                <Button
                                    size="lg"
                                    className="w-full"
                                    onClick={saveOpenAIKeyBtnTapped}
                                    disabled={!openaiApiKeyValue.trim() || isOpenAIKeyUpdating}
                                >
                                    {isOpenAIKeyUpdating ? (
                                        <SharedSpinner />
                                    ) : (
                                        t('workspace.setting.openaiApiKey.saveButton')
                                    )}
                                </Button>
                            ) : (
                                <Button className="w-full" onClick={() => setOpenaiApiKeyEditMode(true)}>
                                    {t('workspace.setting.openaiApiKey.editButton')}
                                </Button>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Sheet open={isOpenAIKeyInfoOpen} onOpenChange={setIsOpenAIKeyInfoOpen}>
                <SheetContent className="overflow-auto" aria-describedby={undefined}>
                    <SheetHeader className="justify-end">
                        <SheetClose>
                            <X className="w-5 h-5" />
                        </SheetClose>
                    </SheetHeader>
                    <OpenAIKeyInfoContent />
                </SheetContent>
            </Sheet>
        </>
    );
};
